import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import LibraryScopeFilter, {
  useLibraryScope,
} from '../components/LibraryScopeFilter';
import {
  AlphaFilterBar,
  AlphaFilterButton,
  AlbumsCard,
  AlbumsList,
  AlbumsListArtist,
  AlbumsListTitle,
  AlbumsPageLayout,
  AlbumsSearchInput,
  AlbumsSectionCard,
  AlbumsStatusText,
} from '../components/styled';

const ALPHA_RANGES = [
  { label: 'ABC', value: 'abc', letters: 'ABC' },
  { label: 'DEF', value: 'def', letters: 'DEF' },
  { label: 'GHI', value: 'ghi', letters: 'GHI' },
  { label: 'JKL', value: 'jkl', letters: 'JKL' },
  { label: 'MNO', value: 'mno', letters: 'MNO' },
  { label: 'PQR', value: 'pqr', letters: 'PQR' },
  { label: 'STU', value: 'stu', letters: 'STU' },
  { label: 'VWX', value: 'vwx', letters: 'VWX' },
  { label: 'YZ', value: 'yz', letters: 'YZ' },
  { label: '#', value: 'other', letters: '' },
];

function matchesAlphaRange(title, alphaRange) {
  if (!alphaRange) {
    return true;
  }

  const firstCharacter = (title || '').trim().charAt(0).toUpperCase();
  const range = ALPHA_RANGES.find((currentRange) => currentRange.value === alphaRange);

  if (!range) {
    return true;
  }

  if (range.value === 'other') {
    return !/^[A-Z]$/.test(firstCharacter);
  }

  return range.letters.includes(firstCharacter);
}

function buildAlbumRoute(albumId, query, alphaRange, scope, bandId) {
  const params = new URLSearchParams();
  if (query) params.set('albumsQ', query);
  if (alphaRange) params.set('albumsAlpha', alphaRange);
  if (scope === 'band' && bandId) {
    params.set('scope', 'band');
    params.set('bandId', bandId);
  }
  const search = params.toString();
  return `/library/albums/${albumId}${search ? `?${search}` : ''}`;
}

function AlbumsPage() {
  const [albums, setAlbums] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const query = searchParams.get('q') || '';
  const alphaRange = searchParams.get('alpha') || '';
  const libraryScope = useLibraryScope(searchParams, setSearchParams);

  useEffect(() => {
    if (libraryScope.isLoadingScopeOptions) return undefined;
    let isActive = true;

    const loadAlbums = async () => {
      setIsLoading(true);

      try {
        const data = await libraryScope.libraryApi.getAlbums();

        if (!isActive) {
          return;
        }

        const nextAlbums = [...(data?.albums || [])].sort((leftAlbum, rightAlbum) =>
          (leftAlbum.title || '').localeCompare(rightAlbum.title || '')
        );

        setAlbums(nextAlbums);
        setErrorMessage('');
      } catch (error) {
        if (isActive && error.status !== 401) {
          setAlbums([]);
          setErrorMessage(error.message || 'Failed to load albums.');
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadAlbums();

    return () => {
      isActive = false;
    };
  }, [libraryScope.isLoadingScopeOptions, libraryScope.libraryApi]);

  const trimmedQuery = query.trim().toLowerCase();
  const filteredAlbums = useMemo(
    () =>
      albums.filter((album) => {
        if (!matchesAlphaRange(album.title || '', alphaRange)) {
          return false;
        }

        if (!trimmedQuery) {
          return true;
        }

        const haystack = [album.title || '', album.artist || '']
          .join(' ')
          .toLowerCase();

        return haystack.includes(trimmedQuery);
      }),
    [albums, alphaRange, trimmedQuery]
  );

  const handleSearchChange = (event) => {
    const nextQuery = event.target.value;
    const nextSearchParams = new URLSearchParams(searchParams);

    if (nextQuery) {
      nextSearchParams.set('q', nextQuery);
    } else {
      nextSearchParams.delete('q');
    }

    setSearchParams(nextSearchParams, { replace: true });
  };

  const handleAlphaChange = (nextAlphaRange) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    const resolvedAlphaRange =
      alphaRange === nextAlphaRange ? '' : nextAlphaRange;

    if (resolvedAlphaRange) {
      nextSearchParams.set('alpha', resolvedAlphaRange);
    } else {
      nextSearchParams.delete('alpha');
    }

    setSearchParams(nextSearchParams, { replace: true });
  };

  return (
    <AlbumsPageLayout>
      <AlbumsSectionCard>
        <LibraryScopeFilter {...libraryScope} />

        {libraryScope.bandsError ? (
          <AlbumsStatusText $error>{libraryScope.bandsError}</AlbumsStatusText>
        ) : null}

        <AlbumsSearchInput
          type="search"
          value={query}
          onChange={handleSearchChange}
          placeholder="Search albums"
          aria-label="Search albums"
        />

        <AlphaFilterBar aria-label="Filter albums alphabetically">
          {ALPHA_RANGES.map((range) => (
            <AlphaFilterButton
              key={range.value}
              type="button"
              $active={alphaRange === range.value}
              onClick={() => handleAlphaChange(range.value)}
            >
              {range.label}
            </AlphaFilterButton>
          ))}
        </AlphaFilterBar>

        {errorMessage ? <AlbumsStatusText $error>{errorMessage}</AlbumsStatusText> : null}
        {!errorMessage && isLoading ? <AlbumsStatusText>Loading albums...</AlbumsStatusText> : null}
        {!errorMessage && !isLoading && filteredAlbums.length === 0 ? (
          <AlbumsStatusText>
            {trimmedQuery
              ? 'No albums matched your search.'
              : alphaRange
                ? 'No albums matched this letter range.'
              : libraryScope.scope === 'band'
                ? 'No albums have been created for this band yet.'
                : 'No albums have been created yet.'}
          </AlbumsStatusText>
        ) : null}

        {filteredAlbums.length > 0 ? (
          <AlbumsList>
            {filteredAlbums.map((album) => (
              <AlbumsCard
                key={album._id}
                as={Link}
                to={buildAlbumRoute(
                  album._id,
                  query,
                  alphaRange,
                  libraryScope.scope,
                  libraryScope.selectedBand?._id
                )}
              >
                <AlbumsListTitle>
                  {album.title || 'Untitled album'}{' '}
                  <AlbumsListArtist>
                    ({album.artist || 'Unknown artist'})
                  </AlbumsListArtist>
                </AlbumsListTitle>
              </AlbumsCard>
            ))}
          </AlbumsList>
        ) : null}
      </AlbumsSectionCard>
    </AlbumsPageLayout>
  );
}

export default AlbumsPage;
